import _ from 'lodash';
import { useObserver } from 'mobx-react-lite';
import moment from 'moment';
import React, { Fragment } from 'react';
import styled from 'styled-components';

import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';
import useCart from 'hooks/use-cart';
import useTranslation from 'hooks/use-translation';
import {
  dispensaryHoursForDay,
  getAcceptedPaymentOptions,
  getAcceptedPaymentOptionsV2,
  getDeliveryFee,
  getDeliveryFeeV2,
  acceptingAfterHoursForDelivery,
  acceptingAfterHoursForDeliveryV2,
  acceptingAfterHoursForPickup,
  acceptingAfterHoursForPickupV2,
} from 'shared/helpers/dispensaries';
import {
  getEnabledV2OrderTypes,
  openInfoForDispensary,
  openInfoForDispensaryV2,
} from 'shared/core/helpers/dispensaries';
import { getTranslationForOrderType } from 'shared/helpers/utils';
import StoreInfoDeliveryIcon from 'src/assets/store-info-delivery-icon';
import StoreInfoPaymentIcon from 'src/assets/store-info-payment-icon';
import StoreInfoMenusIcon from 'src/assets/store-info-menus-icon';
import { paymentMethodsToDisplayNames } from 'shared/constants';
import { Typography } from 'src/typography';

export default function StoreSidebar({ dispensary }) {
  const { t } = useTranslation();
  const Cart = useCart();
  const useOrderTypesConfigV2 = useDispensaryFlag(`rollout.use-updated-ordering-configurations`, dispensary.id);

  const recDispensary = useObserver(() => dispensary.recDispensary);
  const medicalDispensary = useObserver(() => dispensary.medicalDispensary);
  const effectiveHours = useObserver(() => dispensary.effectiveHours);
  const offerDelivery = useObserver(() =>
    useOrderTypesConfigV2 ? dispensary.orderTypesConfigV2.offerDeliveryService : dispensary.offerDelivery
  );
  const offerPickup = useObserver(() => dispensary.offerAnyPickupService);
  const feeTiers = useObserver(() => dispensary.feeTiers);
  const status = useObserver(() => dispensary.status);

  const deliveryInfo = useObserver(() => dispensary.deliveryInfo);

  /**
   * Fee tiers may exist in dispensary data and not be in use.
   * Fees from fee tiers do not apply if a delivery zone or zip code
   * has a fee. They do apply if a zone or zip code is in
   * use, does not have a fee defined, and fee tiers exist.
   * This is handled in getDeliveryInfoForAddress
   */
  const shouldDisplayFeeTierInfo = !_.isFinite(deliveryInfo?.fee) && !_.isFinite(deliveryInfo?.percentFee);

  const { pickup, delivery } = openInfoForDispensary(dispensary, {});
  const pickupStatus = pickup.isOpen && status === 'open' ? t('common.open', 'open') : t('common.closed', 'closed');
  const deliveryStatus = delivery.isOpen && status === 'open' ? t('common.open', 'open') : t('common.closed', 'closed');
  const deliveryFee = useOrderTypesConfigV2
    ? getDeliveryFeeV2(dispensary, Cart.hasDeliveryAddress())
    : getDeliveryFee(dispensary, Cart.hasDeliveryAddress());

  let closedButAvailableForPreorder;

  if (useOrderTypesConfigV2) {
    closedButAvailableForPreorder =
      acceptingAfterHoursForDeliveryV2(dispensary) || acceptingAfterHoursForPickupV2(dispensary);
  } else {
    closedButAvailableForPreorder =
      acceptingAfterHoursForDelivery(dispensary) || acceptingAfterHoursForPickup(dispensary);
  }

  const nowDayOfWeek = moment().format(`dddd`);
  const acceptedOptions = useOrderTypesConfigV2
    ? getAcceptedPaymentOptionsV2(dispensary)
    : getAcceptedPaymentOptions(dispensary);
  const paymentOptions = _.keys(acceptedOptions);
  const paymentLabels = _.uniq(
    // eslint-disable-next-line lodash/prefer-lodash-method
    paymentOptions.map((opt) => (opt === 'payOnlineMerrco' ? `Credit Card` : paymentMethodsToDisplayNames[opt]))
  );

  let hideHours = !!dispensary?.featureFlags.hideStoreHours;
  let enabledV2OrderTypes, openInfoForV2OrderTypes;

  if (useOrderTypesConfigV2) {
    enabledV2OrderTypes = getEnabledV2OrderTypes(dispensary);
    openInfoForV2OrderTypes = openInfoForDispensaryV2(dispensary);
    // If all order types are disabled, hide today's hour section
    hideHours = enabledV2OrderTypes.length === 0;
  }

  return (
    <SidebarContent wide={useOrderTypesConfigV2}>
      {!hideHours && (
        <Fragment>
          <Title size='xsmall' tag='h2'>
            {(props) => props.t('common.today', 'Today')}
          </Title>
          <HoursToday>
            {useOrderTypesConfigV2 && (
              <Fragment>
                {
                  // eslint-disable-next-line lodash/prefer-lodash-method
                  enabledV2OrderTypes.map((orderType) => {
                    const orderTypeStatus =
                      openInfoForV2OrderTypes[orderType].isOpen && status === 'open'
                        ? t('common.open', 'open')
                        : t('common.closed', 'closed');
                    const orderTypeEffectiveHours = dispensary.hoursSettings[orderType].effectiveHours;
                    return (
                      <Hours className={orderTypeStatus} key={`${orderType}-open-info`}>
                        <TypeLabel wide>{getTranslationForOrderType(t, orderType, true)}:</TypeLabel>
                        <Time>{dispensaryHoursForDay(orderTypeEffectiveHours[nowDayOfWeek])}</Time>
                        <StatusPill className={orderTypeStatus} narrow>
                          {orderTypeStatus}
                        </StatusPill>
                      </Hours>
                    );
                  })
                }
              </Fragment>
            )}
            {!useOrderTypesConfigV2 && (
              <Fragment>
                {offerPickup && (
                  <Hours className={pickupStatus}>
                    <TypeLabel>{t('common.pickup', 'Pickup')}:</TypeLabel>
                    <Time>{dispensaryHoursForDay(effectiveHours.pickupHours[nowDayOfWeek])}</Time>
                    <StatusPill className={pickupStatus}>{pickupStatus}</StatusPill>
                  </Hours>
                )}
                {offerDelivery && (
                  <Hours className={deliveryStatus}>
                    <TypeLabel>{t('common.delivery', 'Delivery')}:</TypeLabel>
                    <Time>{dispensaryHoursForDay(effectiveHours.deliveryHours[nowDayOfWeek])}</Time>
                    <StatusPill className={deliveryStatus}>{deliveryStatus}</StatusPill>
                  </Hours>
                )}
              </Fragment>
            )}
            {closedButAvailableForPreorder && (
              <Hours>
                <Preorder>{t('common.closedAfterHoursAvailable', 'Closed, but preordering is available!')}</Preorder>
              </Hours>
            )}
          </HoursToday>
          <Divider />
        </Fragment>
      )}

      <Title size='xsmall' tag='h2'>
        {(props) => props.t('common.storeInfo', 'Store Info')}
      </Title>
      <Container>
        {offerDelivery && (
          <ContainerRow>
            <StoreInfoDeliveryIcon />
            <Info>
              <Caps>{t('common.delivery', 'Delivery')}</Caps>
              {!deliveryFee && t('common.noDeliveryFee', 'Free delivery')}
              {deliveryFee &&
                deliveryFee !== `varies` &&
                shouldDisplayFeeTierInfo &&
                feeTiers.length > 1 &&
                t('common.feeNotice', 'Delivery fee varies by order size.')}
              {deliveryFee &&
                deliveryFee !== `varies` &&
                shouldDisplayFeeTierInfo &&
                feeTiers.length === 1 &&
                `${deliveryFee} ${t('common.deliveryFee', 'Delivery fee.')}`}
              {deliveryFee === `varies` &&
                !shouldDisplayFeeTierInfo &&
                `${t('common.deliveryFee', 'Delivery fee varies by location.')}`}
              {deliveryFee &&
                deliveryFee !== `varies` &&
                !shouldDisplayFeeTierInfo &&
                `${deliveryFee} ${t('common.deliveryFee', 'Delivery fee.')}`}
            </Info>
          </ContainerRow>
        )}

        <ContainerRow>
          <StoreInfoPaymentIcon />
          <Info>
            <Caps>{t('common.payment', 'Payment')}</Caps>
            {paymentLabels.join(', ')}
          </Info>
        </ContainerRow>

        <ContainerRow>
          <StoreInfoMenusIcon />
          <Info>
            <Caps>{t('common.menus', 'Menus')}</Caps>
            {medicalDispensary && t('common.medical', 'Medical')}
            {medicalDispensary && recDispensary && ', '}
            {recDispensary && t('common.recreational', 'Recreational')}
          </Info>
        </ContainerRow>
      </Container>
    </SidebarContent>
  );
}

const Container = styled.div`
  margin-top: 30px;
`;

const Caps = styled.div`
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[45]};
  font-size: 13px;
  line-height: 13px;
  font-weight: bold;
  margin-bottom: 4px;
`;

const Info = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[30]};
  line-height: 130%;
`;

const ContainerRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 25px;

  > svg {
    margin-right: 20px;
    min-width: 39px;
  }

  :last-of-type {
    margin-bottom: 0;
  }
`;

const Divider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.blueGrey[90]};
  margin: 30px 0;
`;

const StatusPill = styled.div`
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.grey[70]};
  color: ${({ theme }) => theme.colors.white} !important;
  text-transform: uppercase;
  font-weight: bold;
  font-size: 10px;
  line-height: 11px;
  padding: 5px 11px 4px;
  display: inline-block;
  margin-left: 10px;

  ${({ theme }) => theme.breakpoints.between(`md`, `md`)} {
    padding: ${({ narrow }) => narrow && `5px 8px 4px`};
    margin-left: ${({ narrow }) => narrow && `5px`};
  }

  &.open {
    background: ${({ theme }) => theme.colors.lime[45]};
  }
`;

const Preorder = styled.div`
  font-size: 13px;
  font-weight: bold;
  line-height: 165%;
  color: ${({ theme }) => theme.colors.orange[65]};
  margin-top: 14px;
`;

const HoursToday = styled.div`
  color: ${({ theme }) => theme.colors.primaryBlack};
  font-size: 13px;
  line-height: 31px;

  .closed {
    color: ${({ theme }) => theme.colors.grey[45]};
  }
`;

const TypeLabel = styled.span`
  font-weight: bold;
  min-width: ${({ wide }) => (wide ? `75px` : `56px`)};
  display: inline-block;

  @media (min-width: 360px) {
    min-width: ${({ wide }) => !wide && `65px`};
  }

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    min-width: ${({ wide }) => wide && `70px`};
  }

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    min-width: ${({ wide }) => wide && `80px`};
  }
`;

const Time = styled.span``;

const Hours = styled.div`
  :first-of-type {
    margin-top: 13px;
  }
`;

const Title = styled(Typography.Heading)`
  color: ${({ theme }) => theme.colors.primaryBlack};
`;

const SidebarContent = styled.div`
  background: ${({ theme }) => theme.colors.grey[95]};
  border-radius: 12px;
  width: 100%;
  padding: 30px 20px;
  margin-top: 30px;
  height: fit-content;
  margin-left: 25px;
  max-width: calc(100% - 50px);

  @media (min-width: 360px) {
    padding: ${({ wide }) => (wide ? `30px 25px` : `30px 35px`)};
    margin: 30px auto 0;
  }

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    padding: 30px 40px;
  }

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    flex-basis: 30%;
    padding: ${({ wide }) => (wide ? `30px 25px 41px 25px` : `30px 33px 41px 33px`)};
    margin: 0 auto;
    max-width: 315px;
  }

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding: 30px 33px 41px 33px;
  }
`;
